import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import "@fontsource/inter";
import "@fontsource/inter/600.css";
import styled from "styled-components";

import useAuth from "../../../../../hooks/useAuth";
import useCurrency from "../../../../../hooks/useCurrency";
import {
  fetchComments,
  fetchProductAsinFeatures,
  fetchProductBsr,
  fetchProductDeliveryTimes,
  fetchProductItemDetails,
  fetchProductList,
  fetchProductPredictedUnits,
  fetchProductProfitability,
  fetchProductRanks,
  fetchProductSales,
  fetchProductSeasonality,
  fetchProductUnitEconomics,
  fetchProductsLongTermStorageData,
  fetchRuleEngineDecisions,
  storeAnalyticsSelector,
} from "../../../../../redux/slices/store_analytics";
import { trackEvent } from "../../../../../utils/analytics";
import { isAtomicUser } from "../../constants";
import {
  FiltersValueTextField,
  StyledSelect,
} from "../StoreProductDetails/BusinessReportAdvanced/styles";
import {
  ColumnTitle,
  EmptyProductListMsg,
  TableStyled,
} from "../StoreProducts/styles";

import { ProductRowDetailed, getTableDailyData } from "./AllProductsTableRow";
import Summaries from "./Summaries";
import VelocityFilterChips, {
  getVelocityStatus,
  matchesVelocityFilter,
} from "./VelocityFilterChips";

const CustomCheckbox = styled(Checkbox)`
  & .MuiSvgIcon-root {
    font-size: 22px;
  }
`;

const CustomInput = styled(FormControlLabel)`
  margin-left: 5px;
  & .MuiFormControlLabel-label {
    font: 300 12px "Inter", sans-serif;
  }
`;

// Unit economics now returns fulfillment as an object (`fulfillment_info`),
// which CSVLink stringifies as "[object Object]". Flatten nested fee data
// to scalars so the export stays numeric.
const flattenUnitEconomicsForExport = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return {};
  }

  const row = { ...data };
  const fulfillmentInfo = row.fulfillment_info;
  const fulfillmentFee = row.fulfillment_fee;

  const fulfillmentSource =
    fulfillmentInfo && typeof fulfillmentInfo === "object"
      ? fulfillmentInfo
      : fulfillmentFee && typeof fulfillmentFee === "object"
      ? fulfillmentFee
      : null;

  if (fulfillmentSource) {
    row.fulfillment_fee = fulfillmentSource.fee ?? 0;
    delete row.fulfillment_info;
  }

  Object.keys(row).forEach((key) => {
    const value = row[key];
    if (value && typeof value === "object") {
      row[key] = value.fee != null ? value.fee : "";
    }
  });

  return row;
};

// Rank daily cells can be objects ({ organic, sponsored, historical }) which
// CSVLink stringifies as "[object Object]". Mirror the on-screen table: show
// the organic rank ("-" when 0/absent) and annotate the sponsored rank.
const formatRankCellForExport = (cell) => {
  const organic = cell.organic || 0;
  const organicText = organic === 0 ? "-" : organic;
  return cell.sponsored ? `${organicText} (S:${cell.sponsored})` : organicText;
};

// Monetary fields in the unit economics export that must be converted to the
// currency currently selected on screen (values arrive from the API in USD).
const UNIT_ECONOMICS_MONETARY_FIELDS = [
  "price",
  "cogs",
  "referral_fee",
  "fulfillment_fee",
  "storage_fee",
  "vat_amount",
];

const TableCustom = ({ style, value }) => {
  const baseColumnTitle = "All store Products in the last two months";

  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const highlightAsin = searchParams.get("asin");
  const shouldExpandAll = searchParams.has("asin");
  const { convertCurrency, getCurrencySymbol } = useCurrency();
  const { user } = useAuth();

  const {
    productItemDetailsList,
    productItemDetailsLoading,
    productListData,
    productListLoading,
    productSalesList,
    productSalesLoading,
    productSalesFreshness,
    storeAnalysisRefreshKey,
    productUnitEconomicsList,
    productUnitEconomicsLoading,
    productProfitabilityList,
    productProfitabilityLoading,
    productRanksList,
    productRanksLoading,
    productLongTermStorageData,
    productLongTermStorageDataLoading,
    productPredictedUnitsList,
    productPredictedUnitsLoading,
    productCommentsData,
    ruleEngineDecisionsData,
    ruleEngineDecisionsLoading,
    productAsinFeaturesData,
    productAsinFeaturesLoading,
    productBsrData,
    productSeasonalityData,
    productDeliveryTimesData,
    productDeliveryTimesLoading,
  } = useSelector(storeAnalyticsSelector);

  const filterAttributes = [
    "Days of supply",
    "Unit economics",
    "Realistic stock",
    "Product velocity",
    "Velocity change",
  ];
  const filterActions = ["greater than", "less than", "equal to"];

  // Predefined filters configuration
  const predefinedFilters = useMemo(
    () => [
      {
        id: "recommendations",
        label: "Recommendations",
        description: "Products with agent recommendations",
        filters: [{ attr: "Has recommendation", action: "is", value: "yes" }],
        icon: "/assets/custom-suggestion-icon-transparent.png",
        customStyle: {
          backgroundColor: "#fef3c7",
          color: "#92400e",
          fontWeight: 600,
          borderColor: "#fcd34d",
          "&:hover": {
            backgroundColor: "#fde68a",
          },
        },
        activeStyle: {
          backgroundColor: "#f59e0b",
          color: "#ffffff",
          borderColor: "#f59e0b",
          "&:hover": {
            backgroundColor: "#d97706",
          },
        },
      },
      {
        id: "inactive",
        label: "INACTIVE",
        description: "Products with inactive status",
        filters: [{ attr: "Status", action: "is", value: "Inactive" }],
        customStyle: {
          backgroundColor: "#e2e8f0",
          color: "#64748b",
          fontWeight: 600,
          fontSize: "10px",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          borderColor: "#cbd5e1",
          "&:hover": {
            backgroundColor: "#cbd5e1",
          },
        },
        activeStyle: {
          backgroundColor: "#64748b",
          color: "#ffffff",
          borderColor: "#64748b",
          "&:hover": {
            backgroundColor: "#475569",
          },
        },
      },
      {
        id: "dos_under_30",
        label: "DOS < 30",
        description: "Days of supply under 30",
        filters: [{ attr: "Days of supply", action: "less than", value: 30 }],
      },
    ],
    []
  );

  const [showCustomFilter, setShowCustomFilter] = useState(false);
  const [activePresetId, setActivePresetId] = useState(null);

  const [currStore, setCurrStore] = useState([]);
  const [currMarketplace, setCurrMarketplace] = useState("");
  const [productsList, setProductsList] = useState([]);
  const [openAllRows, setOpenAllRows] = React.useState(shouldExpandAll);
  const hasScrolledToAsin = useRef(false);
  const lastFetchKeyRef = useRef(null);
  const [openAllInventory, setOpenAllInventory] = React.useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const filterAttributeRef = useRef(null);
  const filterActionRef = useRef(null);
  const filterValueRef = useRef(null);
  const [filterFieldsResetFlag, setFilterFieldsResetFlag] = useState(false);
  const [filterErrors, setFilterErrors] = useState({
    filterAttribute: false,
    filterAction: false,
    filterValue: false,
  });

  const [progress, setProgress] = useState(0);
  const [targetProgress, setTargetProgress] = useState(0);
  const [hasReset, setHasReset] = useState(false);

  // Tracked (non-ranks) call completion counts, used to drive the load bar.
  const trackedApiDone = [
    !productDeliveryTimesLoading,
    !productItemDetailsLoading,
    !productListLoading,
    !productSalesLoading,
    !productUnitEconomicsLoading,
    !productProfitabilityLoading,
    !productLongTermStorageDataLoading,
    !productPredictedUnitsLoading,
    !ruleEngineDecisionsLoading,
    !productAsinFeaturesLoading,
  ];
  const completedApiCalls = trackedApiDone.filter(Boolean).length;
  const totalApiCalls = trackedApiDone.length;
  const isAnyTrackedLoading = completedApiCalls < totalApiCalls;

  // Smooth animation effect.
  // For each step the bar animates exactly to the real completed-fraction
  // target (so it respects the discrete per-call steps). Only once we're on
  // the final pending call (~89% step) do we let it slowly creep toward a 99%
  // ceiling, so it never appears stuck while a slow endpoint finishes. The
  // increment shrinks as we approach the ceiling, so the time to gain each
  // additional percent keeps increasing - a decelerating edge toward 100%.
  useEffect(() => {
    // Only animate while something is actually loading; completion (snap to
    // 100) is handled by the updateProgress effect below.
    if (!isAnyTrackedLoading) return;

    // Creep toward 99 only on the last step; otherwise stop at the real step.
    const onLastStep = completedApiCalls === totalApiCalls - 1;
    const ceiling = onLastStep ? 99 : targetProgress;
    if (progress >= ceiling) return;

    const animationInterval = setInterval(() => {
      setProgress((prev) => {
        const diff = ceiling - prev;

        // Close enough: snap to the ceiling.
        if (diff <= 0.3) {
          return ceiling;
        }

        // Decelerating creep toward the ceiling.
        const increment = Math.max(0.15, diff * 0.04);
        return Math.min(prev + increment, ceiling);
      });
    }, 35);

    return () => clearInterval(animationInterval);
  }, [
    isAnyTrackedLoading,
    completedApiCalls,
    totalApiCalls,
    targetProgress,
    progress,
  ]);

  useEffect(() => {
    const updateProgress = () => {
      // NOTE: productRanksLoading is intentionally excluded from the progress
      // calculation. The fetch-products-ranks endpoint can be very slow, so we
      // don't want it to hold the progress bar back; it should reach 100% once
      // every other call finishes. Ranks load in the background and update the
      // table rows reactively.
      const apiCallsStatus = [
        !productDeliveryTimesLoading,
        !productItemDetailsLoading,
        !productListLoading,
        !productSalesLoading,
        !productUnitEconomicsLoading,
        !productProfitabilityLoading,
        !productLongTermStorageDataLoading,
        !productPredictedUnitsLoading,
        !ruleEngineDecisionsLoading,
        !productAsinFeaturesLoading,
      ];

      // Check if any API calls are currently loading (to detect restart)
      const isAnyLoading = [
        productDeliveryTimesLoading,
        productItemDetailsLoading,
        productListLoading,
        productSalesLoading,
        productUnitEconomicsLoading,
        productProfitabilityLoading,
        productLongTermStorageDataLoading,
        productPredictedUnitsLoading,
        ruleEngineDecisionsLoading,
        productAsinFeaturesLoading,
      ].some((loading) => loading === true);

      // Reset progress only once when API calls start (not continuously)
      if (isAnyLoading && !hasReset && (progress > 0 || targetProgress > 0)) {
        setProgress(0);
        setTargetProgress(0);
        setHasReset(true);
        return;
      }

      // Reset the hasReset flag when no API calls are loading
      if (!isAnyLoading && hasReset) {
        setHasReset(false);
      }

      const completedApiCalls = apiCallsStatus.filter(Boolean).length;
      const totalApiCalls = apiCallsStatus.length;
      const newProgress = (completedApiCalls / totalApiCalls) * 100;

      // When all calls are loaded, quit animation immediately
      if (completedApiCalls === totalApiCalls) {
        setProgress(100);
        setTargetProgress(100);
      } else {
        setTargetProgress(newProgress);
      }
    };

    updateProgress();
  }, [
    productItemDetailsLoading,
    productListLoading,
    productSalesLoading,
    productUnitEconomicsLoading,
    productProfitabilityLoading,
    productLongTermStorageDataLoading,
    productPredictedUnitsLoading,
    ruleEngineDecisionsLoading,
    productAsinFeaturesLoading,
    productDeliveryTimesLoading,
    progress,
    targetProgress,
    hasReset,
  ]);

  useEffect(() => {
    if (value.store.endsWith("_client")) {
      value.is_client = true;
      value.store = value.store.replace("_client", "");
    } else {
      value.is_client = false;
    }

    if (value.store === "all") {
      return;
    }

    // Synchronous re-entry guard: currStore/currMarketplace are state and
    // commit asynchronously, so a re-render arriving before they land would
    // pass the guard above and re-dispatch the whole fetch batch (visible as
    // duplicate requests for every endpoint). The ref updates immediately.
    // storeAnalysisRefreshKey is included so the refresh button can force a
    // re-fetch without changing store/marketplace.
    const fetchKey = `${value.store}|${value.marketplace}|${storeAnalysisRefreshKey}`;
    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }
    lastFetchKeyRef.current = fetchKey;

    const fetchData = async () => {
      dispatch(
        fetchProductList({
          store: value.store,
          marketplace: value.marketplace,
          duration: value.compareConditions.current,
          hierarchy: "hierarchy",
        })
      );

      dispatch(
        fetchProductAsinFeatures({
          store: value.store,
          marketplace: value.marketplace,
        })
      );

      dispatch(
        fetchProductBsr({
          store: value.store,
          start_date: value.compareConditions.current.start_date,
          end_date: value.compareConditions.current.end_date,
          marketplace: value.marketplace,
        })
      );

      dispatch(
        fetchProductSeasonality({
          store: value.store,
          marketplace: value.marketplace,
        })
      );

      dispatch(
        fetchProductItemDetails({
          store: value.store,
          marketplace: value.marketplace,
          level: "hirearchy",
        })
      );

      dispatch(
        fetchProductSales({
          store: value.store,
          marketplace: value.marketplace,
          level: "child",
          start_date: value.compareConditions.current.start_date,
          end_date: value.compareConditions.current.end_date,
        })
      );

      dispatch(
        fetchProductRanks({
          store: value.store,
          marketplace: value.marketplace,
          start_date: value.compareConditions.current.start_date,
          end_date: value.compareConditions.current.end_date,
        })
      );

      dispatch(
        fetchProductUnitEconomics({
          store: value.store,
          marketplace: value.marketplace,
          level: "child",
          start_date: value.compareConditions.current.start_date,
          end_date: value.compareConditions.current.end_date,
        })
      );

      dispatch(
        fetchProductProfitability({
          store: value.store,
          marketplace: value.marketplace,
          start_date: value.compareConditions.current.start_date,
          end_date: value.compareConditions.current.end_date,
        })
      );

      dispatch(
        fetchProductsLongTermStorageData({
          store: value.store,
          marketplace: value.marketplace,
        })
      );

      dispatch(
        fetchProductDeliveryTimes({
          store: value.store,
          marketplace: value.marketplace,
          is_client: value.is_client,
        })
      );

      dispatch(
        fetchComments({
          store: value.store,
          marketplace: value.marketplace,
          start_date: value.compareConditions.current.start_date,
          end_date: value.compareConditions.current.end_date,
        })
      );
      dispatch(
        fetchProductPredictedUnits({
          store: value.store,
          marketplace: value.marketplace,
          start_date: value.compareConditions.current.start_date,
        })
      );
    };

    fetchData();
    setCurrStore(value.store);
    setCurrMarketplace(value.marketplace);
  }, [
    dispatch,
    currStore,
    currMarketplace,
    storeAnalysisRefreshKey,
    value,
    user,
  ]); // table rerender on compare conditions change / manual refresh

  // Recommendations are Atomic's own analysis. Gate the request, not the
  // render: fetching and then hiding puts them in the customer's browser.
  //
  // Deliberately NOT part of the fetch batch above. In a fresh window (the
  // Agents tab's "View in Store Analysis" link opens one) the auth context
  // resolves `user` after the store and marketplace are already set, so the
  // batch ran once with user=null, skipped this request, recorded its fetch
  // key — and its re-entry guard then blocked every later pass, leaving the
  // table without any agent cards. Keying this effect on the atomic-user
  // check as well makes the request fire as soon as the user is known.
  const lastDecisionsFetchKeyRef = useRef(null);
  useEffect(() => {
    const store = value.store.endsWith("_client")
      ? value.store.replace("_client", "")
      : value.store;
    if (store === "all" || !isAtomicUser(user?.email)) {
      return;
    }
    const fetchKey = `${store}|${value.marketplace}|${storeAnalysisRefreshKey}`;
    if (lastDecisionsFetchKeyRef.current === fetchKey) {
      return;
    }
    lastDecisionsFetchKeyRef.current = fetchKey;
    dispatch(
      fetchRuleEngineDecisions({ store, marketplace: value.marketplace })
    );
  }, [dispatch, value.store, value.marketplace, storeAnalysisRefreshKey, user]);

  useEffect(() => {
    if (Object.keys(productItemDetailsList).length > 0) {
      setProductsList(productItemDetailsList);
    } else {
      setProductsList([]);
    }
  }, [productItemDetailsList]);

  const isFullyLoaded =
    !productItemDetailsLoading &&
    !productListLoading &&
    !productSalesLoading &&
    !productUnitEconomicsLoading &&
    !productProfitabilityLoading &&
    !productLongTermStorageDataLoading &&
    !productRanksLoading &&
    !productPredictedUnitsLoading &&
    !ruleEngineDecisionsLoading &&
    !productAsinFeaturesLoading;

  useEffect(() => {
    if (
      !highlightAsin ||
      hasScrolledToAsin.current ||
      !isFullyLoaded ||
      productsList.length === 0
    )
      return;

    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-asin="${highlightAsin}"]`);
      if (el) {
        hasScrolledToAsin.current = true;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.transition = "box-shadow 0.3s ease";
        el.style.boxShadow = "0 0 0 3px #376fd0";
        setTimeout(() => {
          el.style.boxShadow = "none";
        }, 3000);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [highlightAsin, isFullyLoaded, productsList]);

  // Velocity tool chips ("all" | "peak" | "offPace"); only one at a time.
  const [velocityFilter, setVelocityFilter] = useState("all");
  // The chips only make sense when the velocity tool returned some ASIN.
  const hasVelocityData =
    Object.keys(productPredictedUnitsList || {}).length > 0;
  // Without the chips there's no way to change the filter, so show everything
  // (e.g. a chip picked on a store with data, then a switch to one without).
  const activeVelocityFilter = hasVelocityData ? velocityFilter : "all";

  const velocityStatuses = useMemo(
    () =>
      productsList.map((product) =>
        getVelocityStatus(product, productPredictedUnitsList)
      ),
    [productsList, productPredictedUnitsList]
  );

  const velocityFilterCounts = useMemo(
    () => ({
      all: productsList.length,
      peak: velocityStatuses.filter((s) => s.inPeakWindow).length,
      offPace: velocityStatuses.filter((s) => s.offPace).length,
    }),
    [productsList, velocityStatuses]
  );

  const visibleProducts = productsList.filter((_, i) =>
    matchesVelocityFilter(activeVelocityFilter, velocityStatuses[i])
  );

  // Compute parent and child ASIN counts
  const { parentCount, childCount } = useMemo(() => {
    const parents = productsList.length;
    const children = productsList.reduce((total, product) => {
      return total + (product.children ? product.children.length : 0);
    }, 0);
    return { parentCount: parents, childCount: children };
  }, [productsList]);

  const relevantProductData = (product) => {
    if (!product.children) {
      return (
        productListData &&
        product.sku.split(",").map((sku) => {
          return { sku: sku.trim(), ...productListData[sku.trim()] };
        })
      );
    } else {
      return (
        productListData &&
        Object.entries(productListData)
          .map(([key, value]) => {
            if (
              product.children.map((c) => c.child_asin).includes(value.asin)
            ) {
              return { ...value, sku: key };
            } else {
              return null;
            }
          })
          .filter(Boolean)
      );
    }
  };

  const relevantCommentsData = (product) => {
    let emptyCommentsData = {
      [product.child_asin]: [],
    };
    if (!productCommentsData) {
      return emptyCommentsData;
    }
    let parentChildAsins = [];
    if (product.children) {
      parentChildAsins = product.children.map((c) => c.child_asin);
      parentChildAsins.push(product.parent_asin);
    } else {
      parentChildAsins.push(product.child_asin);
    }

    return Object.fromEntries(
      Object.entries(productCommentsData).filter(([key]) =>
        parentChildAsins.includes(key)
      )
    );
  };

  const relevantPredictedUnitsData = (product) => {
    let emptyPredictedUnitsData = {
      [product.child_asin]: {},
    };
    if (!productPredictedUnitsList) {
      return emptyPredictedUnitsData;
    }
    let childAsins = [];
    if (product.children) {
      childAsins = product.children.map((c) => c.child_asin);
    } else {
      childAsins.push(product.child_asin);
    }

    return Object.entries(productPredictedUnitsList)
      .filter(([key]) => childAsins.includes(key))
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, emptyPredictedUnitsData);
  };

  const relevantRanksData = (product) => {
    let emptyRanksData = {
      [product.child_asin]: {},
    };
    if (!productRanksList) {
      return emptyRanksData;
    }
    let childAsins = [];
    if (product.children) {
      childAsins = product.children.map((c) => c.child_asin);
    } else {
      childAsins.push(product.child_asin);
    }

    // return productSalesList.filter((item) => item.asin === asin);
    return Object.entries(productRanksList)
      .filter(([key]) => childAsins.includes(key))
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, emptyRanksData);
  };

  const relevantProfitabilityData = (product) => {
    let emptyProfitabilityData = {
      [product.child_asin]: {},
    };
    if (!productProfitabilityList) {
      return emptyProfitabilityData;
    }
    let childAsins = [];
    if (product.children) {
      childAsins = product.children.map((c) => c.child_asin);
    } else {
      childAsins.push(product.child_asin);
    }

    // return productSalesList.filter((item) => item.asin === asin);
    return Object.entries(productProfitabilityList)
      .filter(([key]) => childAsins.includes(key))
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, emptyProfitabilityData);
  };

  const getLast30CalendarMonthsDays = () => {
    //pad month with 0 if less than 10
    const dates = [];
    for (let i = 0; i <= 30; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      dates.push(`${month}-${day}`);
    }
    return dates.reverse();
  };

  const relevantSalesData = (product) => {
    let dates = getLast30CalendarMonthsDays();
    let emptySalesData = {
      [product.child_asin]: {
        // fill last 30 days strings in format "mm-dd"
        // fill with function getLast30CalendarMonthsDays()
        dates: dates,
        units: Array(dates.length).fill(0),
        avg_price: Array(dates.length).fill(0),
        sales: Array(dates.length).fill(0),
        sponsoredUnits: Array(dates.length).fill(0),
        sponsoredSales: Array(dates.length).fill(0),
        sessions: Array(dates.length).fill(0),
        cvr: Array(dates.length).fill(0),
        deals: Array(dates.length).fill(""),
        ppc_b: Array(dates.length).fill(0),
        ppc_d: Array(dates.length).fill(0),
        ppc_p: Array(dates.length).fill(0),
      },
    };
    if (!productSalesList) {
      return emptySalesData;
    }
    let childAsins = [];
    if (product.children) {
      childAsins = product.children.map((c) => c.child_asin);
    } else {
      childAsins.push(product.child_asin);
    }

    // return productSalesList.filter((item) => item.asin === asin);
    return Object.entries(productSalesList)
      .filter(([key]) => childAsins.includes(key))
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, emptySalesData);
  };

  const relevantEconomicsData = (product) => {
    let emptyEconomicsData = {
      [product.child_asin]: {
        date: "",
        price: 0,
        cogs: 0,
        fulfillment_fee: 0,
        referral_fee: 0,
      },
    };
    if (!productUnitEconomicsList) {
      return emptyEconomicsData;
    }
    let childAsins = [];
    if (product.children) {
      childAsins = product.children.map((c) => c.child_asin);
    } else {
      childAsins.push(product.child_asin);
    }

    return Object.entries(productUnitEconomicsList)
      .filter(([key]) => childAsins.includes(key))
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, emptyEconomicsData);
  };

  const relevantLongTermStorageData = (product) => {
    let emptyLongTermStorageData = {};
    let childAsins = [];
    if (product.children) {
      childAsins = product.children.map((c) => c.child_asin);
    } else {
      childAsins.push(product.child_asin);
    }

    if (!productLongTermStorageData) {
      return emptyLongTermStorageData;
    }

    return Object.entries(productLongTermStorageData)
      .filter(([key]) => childAsins.includes(key))
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, emptyLongTermStorageData);
  };

  const relevantDeliveryTimesData = (product) => {
    if (!productDeliveryTimesData || !productDeliveryTimesData.length) {
      return [];
    }
    let childAsins = [];
    if (product.children) {
      childAsins = product.children.map((c) => c.child_asin);
    } else {
      childAsins.push(product.child_asin);
    }
    return productDeliveryTimesData.filter((item) =>
      childAsins.includes(item.child_asin)
    );
  };

  const relevantAsinFeaturesData = (product) => {
    let emptyAsinFeaturesData = {};
    if (!productAsinFeaturesData) {
      return emptyAsinFeaturesData;
    }

    let childAsins = [];
    if (product.children) {
      childAsins = product.children.map((c) => c.child_asin);
    } else {
      childAsins.push(product.child_asin);
    }

    return Object.entries(productAsinFeaturesData)
      .filter(([key]) => childAsins.includes(key))
      .reduce((obj, [key, value]) => {
        obj[key] = value.feature_data;
        return obj;
      }, emptyAsinFeaturesData);
  };

  // Get BSR data for a product (categories tree + historical rank data)
  const relevantBsrData = (product) => {
    if (!productBsrData) {
      return null;
    }

    const asin = product.child_asin || product.parent_asin;
    const bsrForAsin = productBsrData[asin];

    if (!bsrForAsin) {
      return null;
    }

    // Expected format: { categories: [{catId, name}, ...], ranks: { [catId]: [{time, salesRank}, ...] } }
    return bsrForAsin;
  };

  // Weekly seasonality index per child ASIN (the weighted-rank tooltip). The
  // run month and the shared week list are folded into every entry so the
  // row component can draw the chart without carrying the whole response.
  const relevantSeasonalityData = (product) => {
    const byAsin = productSeasonalityData?.asins;
    if (!byAsin) {
      return {};
    }
    const childAsins = product.children
      ? product.children.map((c) => c.child_asin)
      : [product.child_asin];
    return childAsins.reduce((obj, asin) => {
      if (byAsin[asin]) {
        obj[asin] = {
          ...byAsin[asin],
          run_month: productSeasonalityData.run_month,
          week_ends: productSeasonalityData.week_ends,
        };
      }
      return obj;
    }, {});
  };

  const relevantRuleEngineDecisionsData = (product) => {
    let emptyRuleEngineDecisionsData = {};
    if (!ruleEngineDecisionsData) {
      return emptyRuleEngineDecisionsData;
    }
    let childAsins = [];
    if (product.children) {
      childAsins = product.children.map((c) => c.child_asin);
    } else {
      childAsins.push(product.child_asin);
    }
    let decisions = Object.entries(ruleEngineDecisionsData)
      .filter(([asin]) => childAsins.includes(asin))
      .reduce((obj, [asin, item]) => {
        // item can be an array (new format) or a single object (legacy format)
        obj[asin] = item;
        return obj;
      }, emptyRuleEngineDecisionsData);
    return decisions;
  };

  // NOTE: productRanksLoading is intentionally excluded here. The
  // fetch-products-ranks endpoint can be very slow, and we don't want it to
  // block the whole table from rendering. Rank rows update reactively via
  // relevantRanksData() once the ranks data arrives.
  const isDataLoading =
    productItemDetailsLoading ||
    productListLoading ||
    productSalesLoading ||
    productUnitEconomicsLoading ||
    productProfitabilityLoading ||
    productLongTermStorageDataLoading ||
    productPredictedUnitsLoading;

  const resetFilterFields = () => {
    filterAttributeRef.current.value = "";
    filterActionRef.current.value = "";
    filterValueRef.current.value = "";
    setFilterFieldsResetFlag(!filterFieldsResetFlag);
    setFilterErrors({
      filterAttribute: false,
      filterAction: false,
      filterValue: false,
    });
  };

  const addFilter = () => {
    const filterFieldsErrors = {
      filterAttribute: !filterAttributeRef.current.value,
      filterAction: !filterActionRef.current.value,
      filterValue: !filterValueRef.current.value,
    };
    if (
      filterFieldsErrors.filterAttribute ||
      filterFieldsErrors.filterAction ||
      filterFieldsErrors.filterValue
    ) {
      setFilterErrors(filterFieldsErrors);
      return;
    }

    setOpenAllRows(true);
    const newFilter = {
      attr: filterAttributeRef.current.value,
      action: filterActionRef.current.value,
      value: parseFloat(filterValueRef.current.value),
    };
    trackEvent("custom_filter_added", {
      attribute: newFilter.attr,
      action: newFilter.action,
      value: newFilter.value,
      store: value.store,
    });
    setAppliedFilters([...appliedFilters, newFilter]);
    resetFilterFields();
  };

  const removeFilter = (indexToRemove) => {
    const removed = appliedFilters[indexToRemove];
    trackEvent("custom_filter_removed", {
      attribute: removed?.attr,
      action: removed?.action,
      value: removed?.value,
      store: value.store,
    });
    setAppliedFilters(
      appliedFilters.filter((_, index) => index !== indexToRemove)
    );
    setFilterErrors({
      filterAttribute: false,
      filterAction: false,
      filterValue: false,
    });
  };

  const applyPresetFilter = (preset) => {
    if (activePresetId === preset.id) {
      setActivePresetId(null);
      setAppliedFilters([]);
      trackEvent("filter_preset_toggled", {
        preset: preset.id,
        action: "off",
        store: value.store,
      });
    } else {
      setActivePresetId(preset.id);
      setAppliedFilters(preset.filters);
      setOpenAllRows(true);
      setShowCustomFilter(false);
      trackEvent("filter_preset_toggled", {
        preset: preset.id,
        action: "on",
        store: value.store,
      });
    }
  };

  const clearAllFilters = () => {
    trackEvent("filters_cleared", { store: value.store });
    setAppliedFilters([]);
    setActivePresetId(null);
    resetFilterFields();
  };

  const getChildInventoryData = (
    parentAsin,
    childAsin,
    sku,
    productInventoryData,
    productSalesData
  ) => {
    let inventoryData = {
      parent_asin: parentAsin,
      asin: childAsin,
      sku: sku,
      days_of_supply: "",
      total_inventory: "",
      available: "",
      reserved_transfåer: "",
      reserved_processing: "",
      resereved_customer_orders: "",
      inbound_receiving: "",
      inbound_working: "",
      inbound_shipped: "",
      inbound_shipped_pending: "",
      awd_inventory: "",
      awd_inbound: "",
      awd_outbound: "",
      on_the_way_quantity: "",
      on_the_way_ETA: "",
    };

    // if productInventoryData is null or if productInventoryData.fba_report and awd_report are empty objects, return null
    if (
      !productInventoryData ||
      (Object.keys(productInventoryData.fba_report || {}).length === 0 &&
        Object.keys(productInventoryData.awd_report || {}).length === 0)
    ) {
      return null;
    }

    let inventoryFBAData = productInventoryData.fba_report;
    if (inventoryFBAData) {
      inventoryData.reserved = inventoryFBAData.reserved;
      inventoryData.total_inventory =
        inventoryFBAData.available +
        inventoryFBAData.receiving +
        inventoryFBAData.working +
        inventoryFBAData.shipped +
        inventoryFBAData.reserved;
      inventoryData.available = inventoryFBAData.available;
      inventoryData.reserved_transfer = inventoryFBAData.transfer;
      inventoryData.reserved_processing = inventoryFBAData.processing;
      inventoryData.resereved_customer_orders = inventoryFBAData.customerOrders;
      inventoryData.inbound_receiving = inventoryFBAData.receiving;
      inventoryData.inbound_working = inventoryFBAData.working;
      inventoryData.inbound_shipped = inventoryFBAData.shipped;
    }

    let inventoryAWDData = productInventoryData.awd_report;
    if (inventoryAWDData) {
      inventoryData.awd_inventory = inventoryAWDData.quantity;
      inventoryData.awd_inbound = inventoryAWDData.inbound;
      inventoryData.awd_outbound = inventoryAWDData.outbound;
      inventoryData.awd_inbound_eta = inventoryAWDData.inbound_eta;
    }

    let inventory3PLData = productInventoryData["3pl"];
    if (inventory3PLData) {
      inventoryData["3PL_inventory"] = inventory3PLData.quantity;
    }

    let inventoryOnTheWayData = productInventoryData.velocity;
    if (inventoryOnTheWayData) {
      inventoryData.on_the_way_quantity = inventoryOnTheWayData.shipped_qty;
      inventoryData.on_the_way_ETA = inventoryOnTheWayData.date;
    }

    inventoryData.days_of_supply = calcDaysOfSupply(
      inventoryData,
      productSalesData
    );

    return inventoryData;
  };

  const isAnyDateLessThan15Days = (shipped_eta) => {
    const currentDate = new Date();
    return [shipped_eta].some((dateString) => {
      const date = new Date(dateString);
      const diffTime = Math.abs(date - currentDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 15;
    });
  };

  const calcRealisticStock = (inventoryData) => {
    const isOnTheWayRelevant = Boolean(
      inventoryData.on_the_way_ETA &&
        isAnyDateLessThan15Days(inventoryData.on_the_way_ETA)
    );
    const realisticStock =
      inventoryData.available +
      (inventoryData.reserved -
        (inventoryData.resereved_customer_orders || 0)) +
      inventoryData.inbound_receiving +
      (inventoryData.awd_inventory || 0) +
      (inventoryData.awd_outbound || 0) +
      (inventoryData["3PL_inventory"] || 0) +
      (isOnTheWayRelevant ? inventoryData.on_the_way_quantity : 0);
    return realisticStock;
  };

  const calcVelocity = (data, isPrevWeek = false) => {
    let velocity = 0;
    const velocityDays = 7; // assuming velocityDays is defined somewhere
    let velDays = velocityDays + 1; // ignore today
    let startIdx = isPrevWeek ? -(2 * velDays) : -velDays;
    let endIdx = isPrevWeek ? -velDays : -1;

    if (data) {
      if ("units" in data) {
        // child
        const slicedUnits = data.units.slice(startIdx, endIdx);
        const sumUnits = slicedUnits.reduce((a, b) => a + b, 0);
        velocity = sumUnits / (slicedUnits.length || 1);
      } else {
        // parent
        Object.values(data).forEach((value) => {
          velocity += calcVelocity(value, isPrevWeek);
        });
      }
    }
    return velocity;
  };

  const calcDaysOfSupply = (inventoryData, productSalesData) => {
    const realisticStock = calcRealisticStock(inventoryData);
    const velocity = calcVelocity(productSalesData);

    let daysOfSupply =
      velocity !== 0 && realisticStock !== 0
        ? Math.round(realisticStock / velocity)
        : realisticStock === 0
        ? 0
        : Infinity;

    let actionableDaysOfSupply = daysOfSupply;
    if (daysOfSupply >= 60) {
      let actionableStock =
        realisticStock -
        (inventoryData.awd_inventory ?? 0) -
        (inventoryData["3PL_inventory"] ?? 0);
      actionableDaysOfSupply = Math.round(actionableStock / velocity);
    }

    return actionableDaysOfSupply;
  };

  const getChildDailyData = (productChild, parentAsin) => {
    let salesData = relevantSalesData(productChild);
    let profitabilityData = relevantProfitabilityData(productChild);
    let ranksData = relevantRanksData(productChild);
    let predictedUnitsData = relevantPredictedUnitsData(productChild);
    let leaf = true;

    let dailyData = getTableDailyData(
      salesData,
      profitabilityData,
      ranksData,
      predictedUnitsData,
      [],
      leaf,
      currStore,
      false,
      convertCurrency,
      getCurrencySymbol
    );

    return dailyData.map((data) => {
      const newData = Object.fromEntries(
        Object.entries(data)
          .filter(([key]) => key !== "id")
          .map(([key, value]) => {
            if (value && typeof value === "object") {
              if (value.value !== undefined) return [key, value.value];
              if (data.field === "rank")
                return [key, formatRankCellForExport(value)];
              // Never let a raw object leak into the CSV as "[object Object]".
              return [key, ""];
            }
            return [key, value];
          })
      );
      return {
        ...newData,
        parent_asin: parentAsin,
        asin: productChild.child_asin,
      };
    });
  };

  const getReportInventoryMetricsData = (
    productsList,
    productItemDetailsList,
    productListData,
    productSalesData
  ) => {
    return productsList.flatMap((product) => {
      const parentAsin =
        productItemDetailsList.find((item) =>
          item.children?.some(
            (child) => child.child_asin === product.child_asin
          )
        )?.parent_asin || "N/A"; // Fallback value if parent_asin is not found

      return product.children
        ? product.children.flatMap((productChild) =>
            productChild.sku
              .split(",")
              .map((sku) =>
                getChildInventoryData(
                  parentAsin,
                  productChild.child_asin,
                  sku.trim(),
                  productListData[sku.trim()],
                  productSalesData[productChild.child_asin]
                )
              )
              .filter(Boolean)
          )
        : product.sku
            .split(",")
            .map((sku) =>
              getChildInventoryData(
                parentAsin,
                product.child_asin,
                sku.trim(),
                productListData[sku.trim()],
                productSalesData[product.child_asin]
              )
            )
            .filter(Boolean);
    });
  };

  const getReportInventoryMetricsHeaders = () => {
    return [
      "parent_asin",
      "asin",
      "sku",
      "days_of_supply",
      "total_inventory",
      "available",
      "reserved_transfer",
      "reserved_processing",
      "resereved_customer_orders",
      "inbound_receiving",
      "inbound_working",
      "inbound_shipped",
      "awd_inventory",
      "awd_inbound",
      "awd_outbound",
      "3PL_inventory",
      "on_the_way_quantity",
      "on_the_way_ETA",
    ];
  };

  const getReportChildAsinTableData = (
    productsList,
    productItemDetailsList
  ) => {
    return productsList.flatMap((product) => {
      const parentAsin =
        productItemDetailsList.find((item) =>
          item.children?.some(
            (child) => child.child_asin === product.child_asin
          )
        )?.parent_asin || "N/A"; // Fallback value if parent_asin is not found

      return product.children
        ? product.children.flatMap((productChild) =>
            getChildDailyData(productChild, parentAsin)
          )
        : getChildDailyData(product, parentAsin);
    });
  };

  const getReportChildAsinTableHeaders = (productSalesList) => {
    const dates = Object.values(productSalesList || {})[0]?.["dates"] || [];
    return ["parent_asin", "asin", "field", "total", ...dates];
  };

  const getReportUnitEconomicsData = (
    productUnitEconomicsList,
    productItemDetailsList
  ) => {
    let data = Object.entries(productUnitEconomicsList).map(([asin, data]) => {
      const parentAsin =
        productItemDetailsList.find((item) =>
          item.children?.some((child) => child.child_asin === asin)
        )?.parent_asin || "N/A"; // Fallback value if parent_asin is not found

      const row = {
        parent_asin: parentAsin,
        asin,
        ...flattenUnitEconomicsForExport(data),
      };

      // Report values in the currency currently selected on screen.
      UNIT_ECONOMICS_MONETARY_FIELDS.forEach((field) => {
        if (typeof row[field] === "number") {
          row[field] = Number(convertCurrency(row[field]));
        }
      });

      return row;
    });
    return data;
  };

  const getReportUnitEconomicsHeaders = () => {
    const headers = Object.keys(
      flattenUnitEconomicsForExport(
        Object.values(productUnitEconomicsList)?.[0] || {}
      )
    );
    return ["parent_asin", "asin", ...headers];
  };

  const isSmallScreen = useMediaQuery("(max-width:850px)"); // Detect small screens

  return (
    <React.Fragment>
      {progress < 100 && value.store !== "all" && isSmallScreen && (
        <LinearProgress variant="determinate" value={progress} />
      )}
      <Summaries
        productSalesList={productSalesList}
        productProfitabilityList={productProfitabilityList}
        productSalesFreshness={productSalesFreshness}
        isDataLoading={isDataLoading}
      />
      {/* table filters */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          mb: 2,
          p: 1.5,
          backgroundColor: "#f8fafc",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
        }}
      >
        <CustomInput
          control={
            <CustomCheckbox
              checked={openAllRows}
              onChange={(e) => {
                trackEvent("expand_all_products", {
                  expanded: e.target.checked,
                  store: value.store,
                });
                setOpenAllRows(e.target.checked);
              }}
            />
          }
          label={"Expand all products"}
        />
        <CustomInput
          control={
            <CustomCheckbox
              checked={openAllInventory}
              onChange={(e) => {
                trackEvent("expand_all_inventory", {
                  expanded: e.target.checked,
                  store: value.store,
                });
                setOpenAllInventory(e.target.checked);
              }}
            />
          }
          label={"Expand all inventory"}
        />

        <Box
          sx={{
            height: "24px",
            width: "1px",
            backgroundColor: "#cbd5e1",
            mx: 1,
          }}
        />

        <FilterListIcon sx={{ color: "#64748b", fontSize: 20 }} />
        <Typography
          variant="body2"
          sx={{ color: "#475569", fontWeight: 500, mr: 1 }}
        >
          Quick filters:
        </Typography>

        {/* Predefined filter chips */}
        {predefinedFilters.map((preset) => (
          <Chip
            key={preset.id}
            icon={
              preset.icon ? (
                <img
                  src={preset.icon}
                  alt=""
                  style={{
                    width: 16,
                    height: 16,
                    marginLeft: 4,
                  }}
                />
              ) : undefined
            }
            label={preset.label}
            onClick={() => applyPresetFilter(preset)}
            disabled={isDataLoading || currStore.length === 0}
            variant={
              preset.customStyle
                ? "filled"
                : activePresetId === preset.id
                ? "filled"
                : "outlined"
            }
            size="small"
            sx={{
              fontWeight: 500,
              ...(preset.customStyle && activePresetId !== preset.id
                ? preset.customStyle
                : {}),
              ...(preset.activeStyle && activePresetId === preset.id
                ? preset.activeStyle
                : {}),
              ...(!preset.customStyle && {
                "&:hover": {
                  backgroundColor:
                    activePresetId === preset.id ? undefined : "#e2e8f0",
                },
              }),
              ...(!preset.customStyle &&
                activePresetId === preset.id && {
                  backgroundColor: "#1976d2",
                  color: "#ffffff",
                }),
            }}
            title={preset.description}
          />
        ))}

        {/* Custom filter toggle */}
        <Chip
          icon={<TuneIcon sx={{ fontSize: 16 }} />}
          label="Custom filter"
          onClick={() => {
            trackEvent("custom_filter_toggle", {
              is_open: !showCustomFilter,
              store: value.store,
            });
            setShowCustomFilter(!showCustomFilter);
            if (!showCustomFilter && activePresetId) {
              setActivePresetId(null);
              setAppliedFilters([]);
            }
          }}
          disabled={isDataLoading || currStore.length === 0}
          color={showCustomFilter ? "secondary" : "default"}
          variant={showCustomFilter ? "filled" : "outlined"}
          size="small"
          sx={{
            fontWeight: 500,
            "&:hover": {
              backgroundColor: showCustomFilter ? undefined : "#e2e8f0",
            },
          }}
        />

        {/* Clear all filters */}
        {(appliedFilters.length > 0 || activePresetId) && (
          <Tooltip title="Clear all filters">
            <IconButton
              onClick={clearAllFilters}
              size="small"
              sx={{
                ml: "auto",
                color: "#ef4444",
                "&:hover": {
                  backgroundColor: "#fef2f2",
                },
              }}
            >
              <DeleteIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Custom filter panel (collapsible) */}
      <Collapse in={showCustomFilter}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            gap: 1,
            mb: 2,
            p: 2,
            backgroundColor: "#fefce8",
            borderRadius: "8px",
            border: "1px solid #fef08a",
          }}
        >
          <FormControl error={filterErrors.filterAttribute}>
            <StyledSelect
              ref={filterAttributeRef}
              disabled={isDataLoading || currStore.length === 0}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return "Select Attribute";
                }
                return selected;
              }}
              inputProps={{ ref: filterAttributeRef }}
              key={`attribute-${filterFieldsResetFlag}`}
              error={filterErrors.filterAttribute}
            >
              {filterAttributes.map((attr, index) => (
                <MenuItem key={index} value={attr}>
                  {attr}
                </MenuItem>
              ))}
            </StyledSelect>
            {filterErrors.filterAttribute && (
              <FormHelperText style={{ color: "#d32f2f" }}>
                Required
              </FormHelperText>
            )}
          </FormControl>
          <FormControl error={filterErrors.filterAction}>
            <StyledSelect
              ref={filterActionRef}
              disabled={isDataLoading || currStore.length === 0}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return "Select Action";
                }
                return selected;
              }}
              inputProps={{ ref: filterActionRef }}
              key={`action-${filterFieldsResetFlag}`}
              error={filterErrors.filterAction}
            >
              {filterActions.map((action, index) => (
                <MenuItem key={index} value={action}>
                  {action}
                </MenuItem>
              ))}
            </StyledSelect>
            {filterErrors.filterAction && (
              <FormHelperText style={{ color: "#d32f2f" }}>
                Required
              </FormHelperText>
            )}
          </FormControl>
          <FormControl error={filterErrors.filterValue} sx={{ width: "120px" }}>
            <FiltersValueTextField
              label="Value"
              variant="outlined"
              size="small"
              disabled={isDataLoading || currStore.length === 0}
              inputProps={{ ref: filterValueRef }}
              key={`value-${filterFieldsResetFlag}`}
              error={filterErrors.filterValue}
            />
            {filterErrors.filterValue && (
              <FormHelperText style={{ color: "#d32f2f" }}>
                Required
              </FormHelperText>
            )}
          </FormControl>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              addFilter();
              setActivePresetId(null);
            }}
            disabled={isDataLoading || currStore.length === 0}
            sx={{ height: "40px" }}
          >
            Add filter
          </Button>
        </Box>
      </Collapse>

      {/* Applied filters display */}
      {appliedFilters.length > 0 && !activePresetId && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            mb: 2,
            ml: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Active filters:
          </Typography>
          {appliedFilters.map((filter, index) => (
            <Chip
              key={index}
              label={`${filter.attr} ${filter.action} ${filter.value}`}
              onDelete={() => removeFilter(index)}
              size="small"
              sx={{
                backgroundColor: "#dbeafe",
                color: "#1e40af",
                "& .MuiChip-deleteIcon": {
                  color: "#3b82f6",
                  "&:hover": { color: "#1e40af" },
                },
              }}
            />
          ))}
        </Box>
      )}
      {progress < 100 && value.store !== "all" && (
        <Box sx={{ width: "100%", mb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {`Loading: ${Math.round(progress)}%`}
          </Typography>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      )}
      {!isDataLoading && productsList.length > 0 && hasVelocityData && (
        <VelocityFilterChips
          selected={activeVelocityFilter}
          counts={velocityFilterCounts}
          onSelect={(filterId) => {
            trackEvent("velocity_filter_selected", {
              filter: filterId,
              store: value.store,
            });
            setVelocityFilter(filterId);
          }}
        />
      )}
      {/* list of all products in a table */}
      <TableStyled style={style} component={Paper}>
        {isDataLoading ? (
          <>
            {[1, 2, 3, 4, 5].map((item) => (
              <Skeleton
                key={item}
                width={"97%"}
                sx={{ mt: 5, ml: 5 }}
                variant="rectangular"
                height={15}
              />
            ))}
          </>
        ) : (
          <Table>
            {/* table headers */}
            <TableHead>
              <TableRow
                style={{
                  display: "flex",
                }}
              >
                <TableCell
                  style={{
                    padding: "13px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "Inter",
                      fontWeight: 600,
                      color: "#64748B",
                      position: "relative",
                    }}
                    data-testid="table_header"
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <ColumnTitle>{baseColumnTitle}</ColumnTitle>
                      {parentCount > 0 && (
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#94a3b8",
                          }}
                        >
                          ({parentCount} parents, {childCount} children)
                        </span>
                      )}
                    </div>
                  </div>
                </TableCell>
                {productsList.length !== 0 ? (
                  <>
                    <TableCell
                      style={{
                        display: "flex",
                        color: "#64748B",
                        justifyContent: "flex-end",
                        flexGrow: 100,
                      }}
                    >
                      <CSVLink
                        data={getReportInventoryMetricsData(
                          productsList,
                          productItemDetailsList,
                          productListData,
                          productSalesList
                        )}
                        filename={value.store + "_inventory_metrics_report.csv"}
                        headers={getReportInventoryMetricsHeaders()}
                        disabled={isDataLoading}
                        onClick={() =>
                          trackEvent("csv_download", {
                            report_type: "inventory_metrics",
                            store: value.store,
                          })
                        }
                      >
                        <DownloadIcon
                          style={{ marginRight: "5px" }}
                          disabled={isDataLoading}
                        />
                      </CSVLink>
                      {"Inventory report"}
                    </TableCell>
                    <TableCell
                      style={{
                        display: "flex",
                        color: "#64748B",
                        justifyContent: "flex-end",
                        flexGrow: 1,
                      }}
                    >
                      <CSVLink
                        data={getReportChildAsinTableData(
                          productsList,
                          productItemDetailsList
                        )}
                        filename={value.store + "_table_data_report.csv"}
                        headers={getReportChildAsinTableHeaders(
                          productSalesList
                        )}
                        disabled={isDataLoading}
                        onClick={() =>
                          trackEvent("csv_download", {
                            report_type: "sales_table",
                            store: value.store,
                          })
                        }
                      >
                        <DownloadIcon
                          style={{ marginRight: "5px" }}
                          disabled={isDataLoading}
                        />
                      </CSVLink>
                      {"Sales table report"}
                    </TableCell>
                    <TableCell
                      style={{
                        display: "flex",
                        color: "#64748B",
                        justifyContent: "flex-end",
                        flexGrow: 0,
                      }}
                    >
                      <CSVLink
                        data={getReportUnitEconomicsData(
                          productUnitEconomicsList,
                          productItemDetailsList
                        )}
                        filename={value.store + "_unit_economics_report.csv"}
                        headers={getReportUnitEconomicsHeaders(
                          productUnitEconomicsList
                        )}
                        disabled={isDataLoading}
                        onClick={() =>
                          trackEvent("csv_download", {
                            report_type: "unit_economics",
                            store: value.store,
                          })
                        }
                      >
                        <DownloadIcon
                          style={{ marginRight: "5px" }}
                          disabled={isDataLoading}
                        />
                      </CSVLink>
                      {"Unit Economics report"}
                    </TableCell>
                  </>
                ) : null}
              </TableRow>
            </TableHead>
            {/* table body products - productListData */}
            {visibleProducts.length !== 0 ? (
              <TableBody>
                {visibleProducts.map((product, index) => (
                  <ProductRowDetailed
                    // Stable key: filtering reorders rows, and an index key
                    // would hand one product's row state to another.
                    key={product.parent_asin || product.child_asin || index}
                    store={value.store.replace("_client", "")}
                    sku={product.sku}
                    product={product}
                    compareConditions={value.compareConditions}
                    leaf={value.periodicity}
                    openAllRows={openAllRows}
                    openAllInventory={openAllInventory}
                    inventoryData={relevantProductData(product)}
                    salesData={relevantSalesData(product)}
                    unitEconomicsData={relevantEconomicsData(product)}
                    profitabilityData={relevantProfitabilityData(product)}
                    ranksData={relevantRanksData(product)}
                    longTermStorageData={relevantLongTermStorageData(product)}
                    deliveryTimesData={relevantDeliveryTimesData(product)}
                    predictedUnitsData={relevantPredictedUnitsData(product)}
                    ruleEngineDecisionsData={relevantRuleEngineDecisionsData(
                      product
                    )}
                    asinFeaturesData={relevantAsinFeaturesData(product)}
                    comments={relevantCommentsData(product)}
                    filters={appliedFilters}
                    setParentShow={null}
                    marketplace={value.marketplace}
                    bsrData={relevantBsrData(product)}
                    seasonalityData={relevantSeasonalityData(product)}
                  />
                ))}
              </TableBody>
            ) : null}
          </Table>
        )}
        {!isDataLoading &&
          productsList.length > 0 &&
          visibleProducts.length === 0 && (
            <EmptyProductListMsg>
              No products match this filter
            </EmptyProductListMsg>
          )}
        {!productItemDetailsLoading && productItemDetailsList.length === 0 && (
          <EmptyProductListMsg>
            {value === "all"
              ? "Please select a store first!"
              : "No products available"}
          </EmptyProductListMsg>
        )}
      </TableStyled>
    </React.Fragment>
  );
};

export default TableCustom;
